import { Component, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { ModalControlService } from 'src/app/services/modal-control.service';
import * as olProj from 'ol/proj';
import { MapService } from 'src/app/services/map.service';
import { Feature } from 'ol';
import LineString from 'ol/geom/LineString';
import { VectorLayerDMTR } from 'src/app/menu/view-menu/layer';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke } from 'ol/style';
import { Layer } from 'src/app/menu/view-menu/layer';
import { CoordenateService } from 'src/app/services/coordenate.service';
import { TopLevelLayers } from 'src/app/menu/view-menu/top-level-layers';
import { ModalConfirmationService } from 'src/app/services/modal-confirmation.service';
import { RouteSegmentsService } from 'src/app/services/route-segments.service';
import { ConfigurationConstants, FeatureType } from 'src/app/models/EnumTypes';
import { RouteSegmentsCSVContent, RouteSegmentsCsvModel } from 'src/app/models/routeSegmentsCSV';
import { ProcedureCoordinatePointModel } from 'src/app/models/procedure-coordinate-point';
import { CoordinatePointModel } from 'src/app/models/coordinate-point';
import { ProcedureControlService } from 'src/app/services/procedure-control.service';
import { ProcedureModel } from 'src/app/models/procedure';
import { SelectCoordinatePointsControlService } from '../../services/select-coordinate-points-control.service';
import { PointsService } from 'src/app/services/points.service';
import Geometry from 'ol/geom/Geometry';
import { Segment } from 'src/app/models/Segment';
import { Route } from 'src/app/models/Route';
import { CreateProcedureFromRouteSegmentComponent } from '../../create-procedure-from-route-segment/create-procedure-from-route-segment.component';
import { ProjectService } from 'src/app/services/project.service';
import { ImportFormat } from 'src/app/models/importFormat';

@Component({
  selector: 'app-route-segments-csv',
  templateUrl: './route-segments-csv.component.html',
  styleUrls: ['../../../app.component.scss'],
  standalone: false
})
export class RouteSegmentsCsvComponent implements OnInit {

  importFormat: ImportFormat = ImportFormat.CSV_FORMAT;


  constructor(private bsModalRef: BsModalRef,
    private modalService: ModalControlService,
    private mapService: MapService,
    private ngxCsvParser: NgxCsvParser,
    public routeSegmentsService: RouteSegmentsService,
    private popupService: BsModalService,
    private projectService: ProjectService,
    private rcsControl: CoordenateService) {
    this.routeSegmentsService.model = new RouteSegmentsCsvModel();
  }

  ngOnInit(): void {
    this.routeSegmentsService.model.fillTable();
  }

  cancelImport(): void {
    this.bsModalRef.hide();
  }

  saveFile(event: any): void {
    this.routeSegmentsService.model.file = event.target.files[0];
    if (this.routeSegmentsService.model.layerName) {
      return;
    }
    this.routeSegmentsService.model.layerName = event.target.files[0].name.substr(0, event.target.files[0].name.indexOf('.'));
  }

  startImport(): void {
    if (this.routeSegmentsService.model.file == null) {
      this.modalService.showMesssageModal("Error", "You have to choose a file before import it.");
      return;
    }
    if (!this.ngxCsvParser.isCSVFile(this.routeSegmentsService.model.file)) {
      this.modalService.showMesssageModal("Error", 'The inserted file has an invalid extension. Insert a "csv" file.');
      return;
    }
    if (this.routeSegmentsService.model.decimalSeparator == this.routeSegmentsService.model.fieldSeparator) {
      this.modalService.showMesssageModal("Error", 'Please define a different field and decimal separator.');
      return;
    }
    this.ngxCsvParser
      .parse(this.routeSegmentsService.model.file, { header: false, delimiter: this.routeSegmentsService.model.fieldSeparator })
      .subscribe((result: Array<any>) => {
        for (var i = this.routeSegmentsService.model.dataStartingLine - 1; i < result.length; i++) {
          this.routeSegmentsService.model.fileContent.push(RouteSegmentsCSVContent.map(result[i], this.routeSegmentsService.model.table));
        }
        let errors: string[] = this.validateContent(this.routeSegmentsService.model.fileContent);
        if (errors.length > 0) {
          this.modalService.showMesssageModal('Validation Error', errors.reduce((acc, val) => acc.concat(val)));
          return;
        }

        this.routeSegmentsService.model.features = this.routeSegmentsService.createFeaturesFromFileContent();
        this.routeSegmentsService.saveState(this.routeSegmentsService.model.saveData(), this.projectService.activeProject.id);

        if (this.routeSegmentsService.isCreateProcedureFromRouteSegment) {
          this.createProcedureCoordinatePointsFromFileContent();
        } else {
          this.modalService.showMesssageModal("Import successful", "The Route Segments were imported successfully.");
        }
      }, (error: NgxCSVParserError) => {
        this.modalService.showMesssageModal("Error", "Unable to extract information in CSV.");
      },
        () => {
          this.bsModalRef.hide();
        });
  }

  validateContent(fileContent: RouteSegmentsCSVContent[]): string[] {
    let errors: string[] = [];
    fileContent.forEach((line, index) => {
      if (!this.isValidLat(line.startSegmentPointLatitude)) {
        errors.push("Invalid Latitude. Error encountered in line " + (index + this.routeSegmentsService.model.dataStartingLine) + ".\n");
      }
      if (!this.isValidLat(line.endSegmentPointLatitude)) {
        errors.push("Invalid Latitude. Error encountered in line " + (index + this.routeSegmentsService.model.dataStartingLine) + ".\n");
      }
      if (!this.isValidLon(line.startSegmentPointLongitude)) {
        errors.push("Invalid Latitude. Error encountered in line " + (index + this.routeSegmentsService.model.dataStartingLine) + ".\n");
      }
      if (!this.isValidLon(line.endSegmentPointLongitude)) {
        errors.push("Invalid Latitude. Error encountered in line " + (index + this.routeSegmentsService.model.dataStartingLine) + ".\n");
      }
    });
    return errors;
  }

  private isValidLat(lat: string): boolean {
    try {
      let latDouble: number = Number.parseFloat(lat);
      if (latDouble <= ConfigurationConstants.LATITUDE_MAX && latDouble >= ConfigurationConstants.LATITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  private isValidLon(lon: string): boolean {
    try {
      let lonDouble: number = Number.parseFloat(lon);
      if (lonDouble <= ConfigurationConstants.LONGITUDE_MAX && lonDouble >= ConfigurationConstants.LONGITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }


  createProcedureCoordinatePointsFromFileContent(): void {
    let routeSegments: Map<String, Route> = new Map();

    this.routeSegmentsService.isCreateProcedureFromRouteSegment = false;
    let feats: Feature<Geometry>[] = this.routeSegmentsService.createFeaturesFromFileContent();
    feats.forEach(feat => {
      let newSegment: Segment = this.routeSegmentsService.transformFeatureInSegment(feat, this.rcsControl.activeRCS.isactive ? this.rcsControl.activeRCS.epsg : "EPSG:3857");
      if (!routeSegments.get(newSegment.routeDesignation)) {
        let newRoute: Route = new Route();
        newRoute.designation = newSegment.routeDesignation;
        newRoute.segments.push(newSegment);
        routeSegments.set(newSegment.routeDesignation, newRoute);
      } else {
        let route: Route = routeSegments.get(newSegment.routeDesignation);
        route.segments.push(newSegment);
      }
    });

    let continuityErrorOneRoute: boolean = false;
    routeSegments.forEach((value: Route, key: string) => {
      this.routeSegmentsService.calculateRouteSegmentsContinuity(value);
      if (routeSegments.size == 1 && !value.continuous) {
        continuityErrorOneRoute = true;
        this.modalService.showMesssageModal("Create Procedure from Route Segments", "None of the routes corresponding to the selected segments are continuous, please redo the selection.");
        return;
      }
    });

    if (routeSegments.size > 0) {
      if (!continuityErrorOneRoute) {
        const bsModalRef: BsModalRef = this.popupService.show(CreateProcedureFromRouteSegmentComponent, {
          backdrop: 'static' // Prevent closing on backdrop click
        });
        bsModalRef.content.routeSegments = routeSegments;
      }
    } else {
      this.modalService.showMesssageModal("Create Procedure from Route Segments", "Select at least one route segment.");
    }

    this.bsModalRef.hide();
  }

}

